import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FileText, Plus, MessageCircle } from 'lucide-react';
import { gstAPI } from '../../services/api';
import { useGst } from './GstContext';
import GstLinesEditor, { emptyLine } from './GstLinesEditor';
import { openPdf } from './gstPdf';
import GstWhatsappModal from './GstWhatsappModal';
import { dmy, inr, msg, today } from './gstUi';

export default function GstNotesTab() {
  const { call, callRaw } = useGst();
  const [list, setList] = useState({ data: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [invoices, setInvoices] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(null);

  const load = useCallback(() => { call(() => gstAPI.notes({ page })).then(setList).catch((e) => toast.error(msg(e))); }, [call, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    call(() => gstAPI.invoices({ status: 'active', limit: 200 })).then((b) => setInvoices(b.data)).catch(() => {});
    call(() => gstAPI.items()).then((b) => setItems(b.data)).catch(() => {});
  }, [call]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const b = await call(() => gstAPI.createNote({ type: form.type, invoiceId: form.invoiceId, noteDate: form.noteDate, reason: form.reason, lines: form.lines }));
      toast.success(`${b.data.number} created`); setForm(null); load(); openPdf(callRaw, b.data.type, b.data._id);
    } catch (err) { toast.error(msg(err)); } finally { setBusy(false); }
  };

  const inv = form && invoices.find((i) => i._id === form.invoiceId);
  const setF = (p) => setForm({ ...form, ...p });
  return (
    <div className="card" style={{ padding: 16 }}>
      <button className="btn btn-primary" style={{ marginBottom: 12 }} onClick={() => setForm({ type: 'credit', invoiceId: '', noteDate: today(), reason: '', lines: [emptyLine()] })}><Plus size={14} /> New note</button>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['Number', 'Type', 'Date', 'Against invoice', 'Party', 'Total', ''].map((h) => <th key={h} style={{ textAlign: 'left', padding: 6 }}>{h}</th>)}</tr></thead>
          <tbody>
            {list.data.map((r) => (
              <tr key={r._id} style={{ borderTop: '1px solid rgba(128,128,128,.25)' }}>
                <td style={{ padding: 6 }}>{r.number}</td><td style={{ padding: 6 }}>{r.type}</td><td style={{ padding: 6 }}>{dmy(r.noteDate)}</td>
                <td style={{ padding: 6 }}>{r.invoiceNumber}</td><td style={{ padding: 6 }}>{r.party?.name}</td><td style={{ padding: 6 }}>₹{inr(r.totals?.grandTotal)}</td>
                <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                  <button className="btn btn-secondary" onClick={() => openPdf(callRaw, r.type, r._id)}><FileText size={14} /></button>{' '}
                  <button className="btn btn-secondary" title="Send on WhatsApp" onClick={() => setSharing(r)}><MessageCircle size={14} /></button>
                </td>
              </tr>
            ))}
            {list.data.length === 0 && <tr><td colSpan={7} style={{ padding: 12, opacity: 0.6 }}>No notes.</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <button className="btn btn-secondary" disabled={list.page <= 1} onClick={() => setPage(list.page - 1)}>Prev</button>
        <span>Page {list.page} of {list.pages}</span>
        <button className="btn btn-secondary" disabled={list.page >= list.pages} onClick={() => setPage(list.page + 1)}>Next</button>
      </div>
      {sharing && <GstWhatsappModal doc={sharing} kind="note" onClose={() => setSharing(null)} />}
      {form && (
        <div className="modal-overlay">
          <form className="modal" style={{ maxWidth: 1000, width: '95%' }} onSubmit={submit}>
            <div className="modal-header"><h3>New credit / debit note</h3><button type="button" className="modal-close" onClick={() => setForm(null)}>×</button></div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div className="form-group"><label className="form-label">Type</label>
                  <select className="form-control" value={form.type} onChange={(e) => setF({ type: e.target.value })}><option value="credit">Credit note (reduces)</option><option value="debit">Debit note (adds)</option></select></div>
                <div className="form-group" style={{ flex: 1 }}><label className="form-label">Against invoice</label>
                  <select className="form-control" value={form.invoiceId} onChange={(e) => setF({ invoiceId: e.target.value })}>
                    <option value="">Select…</option>{invoices.map((i) => <option key={i._id} value={i._id}>{i.number} · {i.party?.name} · ₹{inr(i.totals?.grandTotal)}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Date</label><input className="form-control" type="date" max={today()} value={form.noteDate} onChange={(e) => setF({ noteDate: e.target.value })} /></div>
              </div>
              <div className="form-group"><label className="form-label">Reason *</label><input className="form-control" value={form.reason} onChange={(e) => setF({ reason: e.target.value })} /></div>
              <GstLinesEditor lines={form.lines} setLines={(lines) => setF({ lines })} items={items} intra={inv ? inv.intraState : true} inclusive={inv ? inv.pricesIncludeGst === true : false} />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={busy || !form.invoiceId}>{busy ? 'Creating…' : 'Create note'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
